/* @HEADER
 * @version 1.0.541 | 2026-08-24
 * @purpose ZVX-DEV-5003 — is the active project foreign to the organization the switcher currently names?
 * @sidecar activeProjectOutOfScope.ts.header.md
 * @layer app | @hex adapter | @ctx kanban
 * @public true
 * @edit careful
 */

import { resolveProjectOrgId, type OrgScopeProject } from './projectOrgScope.ts'

export interface OutOfScopeInput {
  activeProject: OrgScopeProject | null | undefined
  workspaceId: string | null | undefined
  personalWorkspaceId?: string | undefined
}

/**
 * True when the board on screen belongs to an organization other than the one
 * the workspace switcher names.
 *
 * ## The gap this closes
 *
 * ZVX-DEV-4971 moves the active project into the organization just chosen, and
 * deliberately declines when that organization holds no projects: answering
 * with a foreign project would show one organization's content while claiming
 * another. But *staying put* shows exactly the same foreign content, by the
 * other route — and that is what the operator hit on the day ЛКТ was created:
 * "Я зашёл в ЛКТ, а там левое меню было от Личного."
 *
 * Declining to guess was right. Continuing to render the previous project as
 * though it were this organization's was not. The caller uses this to show an
 * empty state instead of a board that lies about whose it is.
 *
 * ## What it deliberately does NOT call foreign
 *
 * Ignorance is not mismatch. With no active workspace — an offline session, or
 * an identity snapshot that has not loaded — and with no personal workspace to
 * resolve an unassigned project against, the answer is `false`. Blacking out a
 * working application on a fact that has not arrived yet would be a worse
 * failure than the one this exists to prevent.
 */
export function isActiveProjectOutOfScope(input: OutOfScopeInput): boolean {
  const { activeProject, workspaceId, personalWorkspaceId } = input
  if (!activeProject) return false
  if (!workspaceId) return false

  const projectOrgId = resolveProjectOrgId(activeProject, personalWorkspaceId)
  if (!projectOrgId) return false

  return projectOrgId !== workspaceId
}
